/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';
import LinkButton from 'flarum/common/components/LinkButton';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import IndexPage from 'flarum/forum/components/IndexPage';
import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';
import DiscussionSkeleton from './DiscussionSkeleton';
import HomeDiscussionsState from '../states/HomeDiscussionsState';

export interface HomeDiscussionsAttrs {
	title: Mithril.Children;
	params?: { filter?: Record<string, unknown>; sort?: string; include?: string[] };
	showBtn?: boolean;
}

export default class HomeDiscussions extends Component<HomeDiscussionsAttrs> {
	discussions!: HomeDiscussionsState;

	oninit(vnode: Mithril.Vnode<HomeDiscussionsAttrs, this>) {
		super.oninit(vnode);

		this.discussions = new HomeDiscussionsState(this.defaultParams, 1, this.limit);
		this.discussions.refresh();
	}

	get defaultParams() {
		const { params } = this.attrs;
		const defaultParams = { include: [], filter: {} };
		return {
			...defaultParams,
			...params,
		};
	}

	get limit(): number {
		return 7;
	}

	get baseClass(): string {
		return 'HomeDiscussions';
	}

	view() {
		return (
			<div className={this.baseClass}>
				{this.titleView()}
				{this.subtitleView()}

				{this.button}

				{/* Show skeleton elements when loading */}
				{this.discussions.isInitialLoading() && (
					<ul className={`${this.baseClass}-discussions`}>{Array.from(Array(this.limit).keys()).map(this.skeletonItem)}</ul>
				)}

				{this.discussions.isEmpty() && <Placeholder text={this.emptyText} />}

				{this.discussions.hasItems() && (
					<ul className={`${this.baseClass}-discussions`}>
						{(this.discussions as unknown as { getAllItems(): Discussion[] }).getAllItems().map(this.listItem)}
					</ul>
				)}

				<LinkButton href={this.allLinkHref} className="LinkButton">
					{this.allLinkText}
				</LinkButton>
			</div>
		);
	}

	titleView() {
		return (
			<h2>
				<span>{this.titleText}</span>
			</h2>
		);
	}

	subtitleView(): Mithril.Children {
		return null;
	}

	get button() {
		const { showBtn } = this.attrs;
		if (!showBtn) {
			return null;
		}

		const allowed = app.forum.attribute('canStartDiscussion') || !app.session.user;

		const label = app.translator.trans(allowed ? 'core.forum.index.start_discussion_button' : 'core.forum.index.cannot_start_discussion_button');

		return (
			<Button
				className="Button Button--large IndexPage-newDiscussion"
				itemClassName="App-primaryControl"
				onclick={IndexPage.prototype.newDiscussionAction.bind(IndexPage.prototype)}
				disabled={!allowed}
			>
				{label}
			</Button>
		);
	}

	get titleText(): Mithril.Children {
		const { title } = this.attrs;
		return title;
	}

	get emptyText() {
		return app.translator.trans('core.forum.discussion_list.empty_text');
	}

	get allLinkText() {
		return app.translator.trans('glowingblue-orell-thalia-common.forum.home.discussions.all_link');
	}

	get allLinkHref(): string {
		return app.route('index');
	}

	listItem(discussion: Discussion) {
		return (
			<li key={discussion.id()} data-id={discussion.id()}>
				<DiscussionListItem discussion={discussion} params={{}} />
			</li>
		);
	}

	skeletonItem() {
		return <DiscussionSkeleton />;
	}
}
